// src/pages/ProjectDetail.js (Updated with template support)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Edit2, Clock,
  GitBranch, Activity, CheckCircle,
  Layers, ArrowUpRight, Sparkles
} from 'lucide-react';
import api from '../services/api';
import PipelineTemplates from '../components/PipelineTemplates';
import EditProjectModal from '../components/EditProjectModal';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [showNewPipelineModal, setShowNewPipelineModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pipelineName, setPipelineName] = useState('');
  const [pipelineDescription, setPipelineDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalNodes: 0,
    completedRuns: 0,
    avgAccuracy: 0,
    lastUpdated: null,
    nodeTypes: {},
    recentActivity: []
  });

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      setIsLoading(true);
      const [projectRes, pipelinesRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/pipelines?projectId=${projectId}`)
      ]);

      const projectData = projectRes.data;
      const pipelinesData = pipelinesRes.data;

      setProject(projectData);
      setPipelines(pipelinesData);

      // Calculate pipeline statistics
      let totalNodes = 0;
      let completedRuns = 0;
      let accuracySum = 0;
      let accuracyCount = 0;
      const nodeTypes = {};
      const recentActivity = [];

      pipelinesData.forEach(pipeline => {
        if (pipeline.elements) {
          totalNodes += pipeline.elements.length;
          
          // Count node types
          pipeline.elements.forEach(element => {
            nodeTypes[element.type] = (nodeTypes[element.type] || 0) + 1;
          });

          const TRAIN_TYPES = new Set([
            'trainClassification', 'trainRegression', 'trainDeepLearning',
            'trainTimeSeries', 'trainClustering', 'trainCNN', 'trainModel',
          ]);
          const trainNodes = pipeline.elements.filter(el =>
            TRAIN_TYPES.has(el.type) && el.data?.status === 'completed'
          );

          completedRuns += trainNodes.length;
          trainNodes.forEach(node => {
            const acc = node.data?.results?.accuracy;
            if (typeof acc === 'number') {
              accuracySum += acc;
              accuracyCount++;
            }
          });

          // Add to recent activity
          recentActivity.push({
            type: 'pipeline_updated',
            name: pipeline.name,
            date: new Date(pipeline.updatedAt).toLocaleDateString(),
            time: new Date(pipeline.updatedAt).toLocaleTimeString(),
          });
        }
      });

      setStats({
        totalNodes,
        completedRuns,
        avgAccuracy: accuracyCount > 0 ? accuracySum / accuracyCount : 0,
        lastUpdated: projectData.updatedAt,
        nodeTypes,
        recentActivity: recentActivity.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5)
      });

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setIsLoading(false);
    }
  };

  const handleCreatePipeline = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/pipelines', {
        name: pipelineName.trim(),
        projectId: projectId
      });
      setPipelines([...pipelines, response.data]);
      setShowNewPipelineModal(false);
      setPipelineName('');
      setPipelineDescription('');
      await fetchProjectData();
    } catch (error) {
      console.error('Error creating pipeline:', error);
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      const response = await api.post('/pipelines', {
        name: template.name,
        projectId,
        templateElements: template.elements
      });
      setShowTemplateModal(false);
      await fetchProjectData();
      navigate(`/pipeline/${response.data._id}`);
    } catch (error) {
      console.error('Error creating pipeline from template:', error);
    }
  };

  const handleCreateBlankPipeline = () => {
    setShowTemplateModal(false);
    setShowNewPipelineModal(true);
  };

  const StatCard = ({ icon: Icon, title, value, description, color }) => (
    <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-gray-600 text-xs font-medium mb-0.5">{title}</div>
          <div className="text-xl font-bold mb-0.5">{value}</div>
          <div className="text-gray-500 text-xs">{description}</div>
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  const PipelineCard = ({ pipeline }) => {
    const nodeCount = pipeline.elements?.length || 0;
    const lastUpdated = new Date(pipeline.updatedAt).toLocaleDateString();
    const TRAIN_TYPES = new Set(['trainClassification', 'trainRegression', 'trainDeepLearning', 'trainTimeSeries', 'trainClustering', 'trainCNN', 'trainModel']);
    const trainNodes = pipeline.elements?.filter(el => TRAIN_TYPES.has(el.type)) || [];
    const status = trainNodes.some(n => n.data?.status === 'completed') ? 'trained' : nodeCount > 0 ? 'configured' : 'empty';

    return (
      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group cursor-pointer"
           onClick={() => navigate(`/pipeline/${pipeline._id}`)}>
        <div className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                {pipeline.name}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Last updated: {lastUpdated}
              </p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium 
              ${status === 'trained' ? 'bg-green-100 text-green-700' :
                status === 'configured' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="flex items-center space-x-1.5 text-xs text-gray-600">
              <Layers size={14} />
              <span>{nodeCount} nodes</span>
            </div>
            <div className="flex items-center space-x-1.5 text-xs text-gray-600">
              <Activity size={14} />
              <span>{trainNodes.length} models</span>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center">
          <div className="flex items-center space-x-1.5 text-xs text-blue-600 font-medium">
            <span>View Pipeline</span>
            <ArrowUpRight size={14} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Project Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">{project?.name}</h1>
              <button
                onClick={() => setShowEditModal(true)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"

>
                <Edit2 size={16} className="text-gray-400" />
                
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {project?.description || 'No description provided'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowTemplateModal(true)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1.5"
            >
              <Sparkles size={16} />
              <span>New Pipeline</span>
            </button>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Layers}
            title="Total Pipelines"
            value={pipelines.length}
            description="Active pipelines"
            color="bg-blue-500"
          />
          <StatCard
            icon={GitBranch}
            title="Total Nodes"
            value={stats.totalNodes}
            description="Across all pipelines"
            color="bg-purple-500"
          />
          <StatCard
            icon={CheckCircle}
            title="Completed Runs"
            value={stats.completedRuns}
            description="Training runs completed"
            color="bg-green-500"
          />
          <StatCard
            icon={Activity}
            title="Average Accuracy"
            value={`${(stats.avgAccuracy * 100).toFixed(1)}%`}
            description="Across all models"
            color="bg-orange-500"
          />
        </div>

        {/* Pipelines Grid */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pipelines</h2>
            <div className="flex items-center space-x-1.5 text-xs text-gray-500">
              <Clock size={14} />
              <span>Last updated {new Date(stats.lastUpdated).toLocaleDateString()}</span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : pipelines.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Sparkles size={20} className="text-white" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1.5">No pipelines yet</h3>
              <p className="text-sm text-gray-500 mb-3">Get started with a template or create from scratch</p>
              <button
                onClick={() => setShowTemplateModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center space-x-1.5 mx-auto"
              >
                <Sparkles size={16} />
                <span>Create Pipeline</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pipelines.map((pipeline) => (
                <PipelineCard key={pipeline._id} pipeline={pipeline} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pipeline Templates Modal */}
      {showTemplateModal && (
        <PipelineTemplates
          onSelectTemplate={handleSelectTemplate}
          onCreateBlank={handleCreateBlankPipeline}
          onClose={() => setShowTemplateModal(false)}
        />
      )}

      {/* Manual Pipeline Creation Modal */}
      {showNewPipelineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create Blank Pipeline</h2>
            <form onSubmit={handleCreatePipeline}>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pipeline Name
                  </label>
                  <input
                    type="text"
                    value={pipelineName}
                    onChange={(e) => setPipelineName(e.target.value)}
                    placeholder="Enter pipeline name"
                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={pipelineDescription}
                    onChange={(e) => setPipelineDescription(e.target.value)}
                    placeholder="Enter pipeline description"
                    rows={3}
                    className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowNewPipelineModal(false)}
                  className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Pipeline
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && project && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSave={(updated) => {
            setProject(updated);
            setShowEditModal(false);
          }}
        />
      )}

    </div>
  );
};

export default ProjectDetail;